import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { captureError } from '@/lib/sentry'

export interface InfrastructureStats {
  vanityTotal: number
  vanityUsed: number
  vanityAvailable: number
  verifiedTokens: number
  pendingFeeRoutes: number
}

export function useInfrastructureStats() {
  return useQuery({
    queryKey: ['infrastructure-stats'],
    queryFn: async (): Promise<InfrastructureStats> => {
      // All queries wrapped in try/catch for RLS resilience
      let vanityTotal = 0
      let vanityUsed = 0
      let vanityAvailable = 0
      let verifiedTokens = 0
      let pendingFeeRoutes = 0

      // 1. Vanity stats via RPC
      try {
        const { data } = await supabase.rpc('get_vanity_stats')
        if (data) {
          const stats = data as { available: number; used: number; total: number }
          vanityTotal = stats.total
          vanityUsed = stats.used
          vanityAvailable = stats.available
        }
      } catch (error) {
        captureError(error instanceof Error ? error : new Error('useInfrastructureStats: vanity stats RPC failed'), {
          feature: 'admin_dashboard',
          component: 'useInfrastructureStats',
        })
      }

      // 2. Verified tokens
      try {
        const { count } = await supabase
          .from('token_verification_requests')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'approved')
        verifiedTokens = count || 0
      } catch (error) {
        captureError(error instanceof Error ? error : new Error('useInfrastructureStats: token verification query failed'), {
          feature: 'admin_dashboard',
          component: 'useInfrastructureStats',
        })
      }

      // 3. Pending fee routes
      try {
        const { count } = await supabase
          .from('pending_fee_routes')
          .select('*', { count: 'exact', head: true })
          .eq('status', 'pending')
        pendingFeeRoutes = count || 0
      } catch (error) {
        captureError(error instanceof Error ? error : new Error('useInfrastructureStats: pending fee routes query failed'), {
          feature: 'admin_dashboard',
          component: 'useInfrastructureStats',
        })
      }

      return {
        vanityTotal,
        vanityUsed,
        vanityAvailable,
        verifiedTokens,
        pendingFeeRoutes,
      }
    },
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  })
}
